import React from 'react';

interface SalesHistoryStatsProps {
  salesData: any[];
  analytics?: any | null;
  /** Response from GET /pharma/api/reporting/tax/sales */
  salesTaxReport?: any | null;
}

function totalTaxFromSalesTaxReport(report: any | null | undefined): number | null {
  if (report == null) return null;
  const raw =
    report.sales_tax_total ??
    report.total_tax ??
    report.totalTax ??
    report.total ??
    report?.data?.total_tax ??
    report?.summary?.total_tax;
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

const SalesHistoryStats: React.FC<SalesHistoryStatsProps> = React.memo(({ salesData, analytics, salesTaxReport }) => {
  // Calculate total revenue
  const totalRevenue = salesData.reduce((sum, sale) => {
    const totalAmount = sale.total_amount || sale.totalAmount || sale.amount || 0;
    return sum + (typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0);
  }, 0);

  const totalOrders = analytics?.total_orders ?? salesData.length;
  const completedOrders = analytics?.completed_orders ?? salesData.filter(sale => {
    const status = sale.status || sale.payment_status || '';
    return status === 'completed';
  }).length;
  const pendingOrders = analytics?.pending_orders ?? salesData.filter(sale => {
    const status = sale.status || sale.payment_status || '';
    return status === 'pending';
  }).length;
  const totalTaxFromApi = totalTaxFromSalesTaxReport(salesTaxReport);
  const totalTax = totalTaxFromApi != null ? totalTaxFromApi : (analytics?.total_tax ?? 0);
  const revenueToShow = analytics?.total_revenue ?? totalRevenue;

  return (
    <section style={{
      marginBottom: '2rem'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 252, 0.96) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          padding: '0.9rem',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', marginBottom: '0.25rem' }}>{totalOrders}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Orders</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 252, 0.96) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          padding: '0.9rem',
          border: '1px solid rgba(59, 130, 246, 0.1)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#3b82f6', marginBottom: '0.25rem' }}>₹{(Number(revenueToShow) || 0).toFixed(2)}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Revenue</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 252, 0.96) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          padding: '0.9rem',
          border: '1px solid rgba(245, 158, 11, 0.1)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#22c55e', marginBottom: '0.25rem' }}>{completedOrders}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Completed Orders</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 252, 0.96) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          padding: '0.9rem',
          border: '1px solid rgba(239, 68, 68, 0.1)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ef4444', marginBottom: '0.25rem' }}>{pendingOrders}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending Orders</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 252, 0.96) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          padding: '0.9rem',
          border: '1px solid rgba(99, 102, 241, 0.1)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#6366f1', marginBottom: '0.25rem' }}>₹{(Number(totalTax) || 0).toFixed(2)}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Tax</div>
        </div>
      </div>
    </section>
  );
}, (prevProps, nextProps) => {
  if (prevProps.analytics !== nextProps.analytics) return false;
  if (prevProps.salesTaxReport !== nextProps.salesTaxReport) return false;

  if (prevProps.salesData !== nextProps.salesData) {
    if (prevProps.salesData.length !== nextProps.salesData.length) {
      return false;
    }
    for (let i = 0; i < prevProps.salesData.length; i++) {
      const prevId = prevProps.salesData[i]?.id || prevProps.salesData[i]?.sale_id;
      const nextId = nextProps.salesData[i]?.id || nextProps.salesData[i]?.sale_id;
      if (prevId !== nextId) {
        return false;
      }
    }
  }

  return true;
});

SalesHistoryStats.displayName = 'SalesHistoryStats';

export default SalesHistoryStats;